import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../../elements/LoadingSpinner';
import useGhostActions from '../../../store/ghost/hooks/useGhostActions';
import useSelectedArticle from '../../../store/ghost/hooks/useSelectedArticle';
import ArticleStyle from './articletheme.styles';
import BodyText from '../../elements/BodyText';
import AppLink from '../../elements/AppLink';
import AdSenseUnit from '../../elements/AdSenseUnit';
import AuthorSection from '../GhostArticles/AuthorSection';
import { ArticleHero, ArticleInfo, ArticleTitle, TitleAndReadingTime, ReadingTime, BackTo, ArticleImage, HeroContent, DefaultStyledAdSenseUnit } from './GhostArticle.styles';
import ArticleMetaTags from './ArticleMetaTags';
/**
 * Handles loading and rendering a single Ghost article. In order for this
 * component to work correctly, you must:
 *
 * - Integrate the `ghost` pantheon redux model
 * - Select the article that you want loaded BEFORE this component is rendered
 *
 * This component is NOT themable
 *
 * ## Customization
 *
 * To add custom fonts, you will need to do so through the `fonts` prop. This prop takes in an object in the form of:
 *
 * ```js
 * {
 *  title: '', // article title
 *  header: '', // any headers within the article
 *  body: '', // article body text
 *  backto: '' // the "Back to all articles" link at the top of the page
 * }.
 *```
 *
 * If not specified, all of the fonts default to Arial.
 *
 * To add custom colors, you will need to do so through the `colors` prop. This prop takes in an object in the form of:
 *
 * ```js
 * {
 *  title: '', // article title
 *  body: '', // article body text
 *  link: '', // any links
 *  header: '', // any headers within the article
 *  quote: '', // quote text within the article
 *  published: '', // published date text
 *  author: '', // author name text
 *  readtime: '', // reading time text
 *  backto: '', // the "Back to all articles" link at the top of the page
 * }
 *```
 *
 * If not specified, all of the colors default to black.
 *
 * You can add any other custom style by adding in a `theme` object to `ThemeProvider`. This extra styling is completely optional and only necessary if specific customizations are necessary. To add custom styles, you'll want to create a `theme` object that should look like this:
 *
 * ```js
 * const theme = {
 *  ghostTitle: ``, // article title
 *  ghostAuthor: ``, // author name text
 *  ghostPublished: ``, // published date text
 *  ghostReadTime: ``, // reading time text
 *  ghostLink: ``, // any links within the article
 *  ghostBackTo: `` // "Back to all articles" link at the top
 * }
 *```
 *
 * To use this theme, you'll need to wrap the GhostArticles component in a theme provider where `theme` is passed in to the theme prop. For more information, read more about theming with ThemeProvider [here](https://styled-components.com/docs/advanced#theming).
 *
 * ## AdSense Integration
 * By passing in an object for the `adsense` prop, you are allowing AdSense integration onto each of your articles. By defualt, two AdSense slots are included on each article: one at the top and one at the bottom. Both ads are responsive banner ads by default; however, any styling customizations can be added through the `style` or `className` key in your object.
 *
 * In order to be able to properly integrate with AdSense, you must first make sure that you've followed the documentation and instructions found [here](https://internal.minerva.dev/pantheon/?path=/docs/components-elements-adsenseunit--default). In order to properly integrate, you must create 2 different ad slots to include on the article pages. Once all of the AdSense setup is done, you'll want to pass an object similar to the one below to the `adsense` prop:
 *
 * ```
 * {
 *   { AdSenseTop:
 *       slot: '',
 *       className: '',
 *       style: {},
 *       format: '',
 *       client: '',
 *       layout: '',
 *       layoutKey: '',
 *       responsive: ''
 *   },
 *   { AdSenseBottom:
 *       slot: '',
 *       className: '',
 *       style: {},
 *       format: '',
 *       client: '',
 *       layout: '',
 *       layoutKey: '',
 *       responsive: '',
 *   },
 * }
 *```
 * If you would like to include both ads, be sure to specifically include `AdSenseTop` and `AdSenseBottom`. If you're looking to only keep one ad, then only include either `AdSenseTop` or `AdSenseBottom`. For each one that you include, the `slot` key with a unique value is required; however, any of the other keys are not mandatory to include in your object if the generated AdSense code does not ask for them. Please refer to [these docs](https://internal.minerva.dev/pantheon/?path=/docs/components-elements-adsenseunit--default) to understand what each of these keys mean and how they are related to the AdSense code.
 *
 * ## Example Usage
 * ```js
 *
 * import { GhostArticle } from '@minerva-analytics/pantheon';
 *
 * const colors = {
 *  body: 'red',
 *  link: 'orange',
 *  title: 'yellow',
 *  header: 'green',
 *  quote: 'blue',
 *  backto: 'indigo',
 *  published: 'violet',
 *  author: 'pink',
 *  readtime: 'lightcoral',
 * };
 *
 * const fonts = {
 *  title: 'Arial Black',
 *  header: 'Arial Black',
 *  body: 'Arial',
 *  backto: 'Arial Black',
 * };
 *
 * const domain = 'https://minervaanalytics.co';
 *
 * const ghostApi = new GhostContentAPI({
 *   url: `https://minerva.minervaarticles.com`,
 *   key: `12345`,
 *   version: `v3`,
 * });
 *
 * <GhostArticle
 *   ghostApi={ghostApi}
 *   domain={domain}
 *   colors={colors}
 *   fonts={fonts}
 * />
 * ```
 */

var GhostArticle = function GhostArticle(props) {
  var _useGhostActions = useGhostActions(props.ghostApi),
      getArticle = _useGhostActions.getArticle;

  var selectedArticle = useSelector(function (state) {
    return state.ghost.selected;
  });
  var status = useSelector(function (state) {
    return state.ghost.getArticleStatus;
  });
  var article = useSelectedArticle(); // If the article has embedded twitter posts, this will make sure that they
  // render correctly

  useEffect(function () {
    if (window.twttr) {
      window.twttr.widgets.load();
    }
  }, [article]);
  useEffect(function () {
    var load = props.getArticle || getArticle;

    if (!article && !status.failed && !status.loading) {
      load(selectedArticle);
    }
  }, []); // Custom logic for Ghost image gallery

  useEffect(function () {
    var images = document.querySelectorAll('.kg-gallery-image img');
    images.forEach(function (image) {
      var container = image.closest('.kg-gallery-image');
      var width = image.attributes.width.value;
      var height = image.attributes.height.value;
      var ratio = width / height;
      container.style.flex = ratio + " 1 0%";
    });
  });

  if (status.failed) {
    return /*#__PURE__*/React.createElement(BodyText, null, props.failureText);
  }

  if (!article || status.loading) {
    return /*#__PURE__*/React.createElement(LoadingSpinner, null);
  }

  var AdSenseTop = props.adsense && props.adsense.AdSenseTop;
  var AdSenseBottom = props.adsense && props.adsense.AdSenseBottom;

  var AdSense = function AdSense(adPosition) {
    var AdSenseUnitComponent = adPosition.className ? AdSenseUnit : DefaultStyledAdSenseUnit;
    return /*#__PURE__*/React.createElement(AdSenseUnitComponent, {
      className: adPosition.className,
      style: adPosition.style,
      client: adPosition.client,
      slot: adPosition.slot,
      format: adPosition.format,
      layout: adPosition.layout,
      layoutKey: adPosition.layoutKey,
      responsive: adPosition.responsive
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ArticleStyle, {
    colors: props.colors,
    fonts: props.fonts
  }), /*#__PURE__*/React.createElement(ArticleMetaTags, {
    article: article,
    domain: props.domain,
    metadata: props.metadata,
    ampArticleData: props.ampArticleData
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AppLink, {
    to: "/articles"
  }, /*#__PURE__*/React.createElement(BackTo, {
    fonts: props.fonts,
    colors: props.colors
  }, "Back to all articles")), AdSenseTop ? AdSense(AdSenseTop) : null, /*#__PURE__*/React.createElement(ArticleHero, null, /*#__PURE__*/React.createElement(HeroContent, null, /*#__PURE__*/React.createElement(ArticleImage, {
    src: article.feature_image,
    alt: article.title
  }), /*#__PURE__*/React.createElement(ArticleInfo, null, /*#__PURE__*/React.createElement(TitleAndReadingTime, null, /*#__PURE__*/React.createElement(ArticleTitle, {
    colors: props.colors,
    fonts: props.fonts
  }, article.title), /*#__PURE__*/React.createElement(ReadingTime, {
    colors: props.colors,
    fonts: props.fonts
  }, article.reading_time === 0 ? 1 : article.reading_time, " min read")), /*#__PURE__*/React.createElement(AuthorSection, {
    articlePage: true,
    article: article,
    fonts: props.fonts,
    colors: props.colors,
    hideAuthor: props.hideAuthor
  })))), /*#__PURE__*/React.createElement("div", {
    className: "post" // eslint-disable-next-line
    ,
    dangerouslySetInnerHTML: {
      __html: article.html
    }
  })), AdSenseBottom ? AdSense(AdSenseBottom) : null);
};

GhostArticle.propTypes = {
  /** The initialized Ghost Content API: https://ghost.org/docs/api/v3/javascript/content/#working-example */
  ghostApi: PropTypes.object.isRequired,

  /** The domain that your site uses (eg. `https://thequickscope.com`). Needed for meta tags + AMP data. */
  domain: PropTypes.string.isRequired,

  /** A custom function to call in order to fetch articles */
  getArticle: PropTypes.func,

  /** The text that a user will see if an article fails to load */
  failureText: PropTypes.string,

  /**
   * Custom data that will be used to generate additional meta tags in the `head` of the document. eg:
   * ```
   *  {
   *    'article:publisher': `https://www.facebook.com/QuickscopeNews/`,
   *    'twitter:site': `https://twitter.com/TheQSNews`,
   *  }
   * ```
   */
  metadata: PropTypes.object,

  /**
   * Custom data that will be used to generate additional AMP data. eg:
   * ```
   * {
   *   publisher: {
   *     '@type': 'Organization',
   *     name: 'Quickscope',
   *     logo:
   *       'https://storage.googleapis.com/quickscope/assets/QuickScope_Logo.png',
   *   }
   * }
   * ```
   */
  ampArticleData: PropTypes.object,

  /** Custom colors to use for the ghost article theme */
  colors: PropTypes.object,

  /** Custom fonts to use for the ghost article theme */
  fonts: PropTypes.object,

  /** Custom object which includes all of the relevant AdSense information for 2 ads. If an object is passed in for this prop, then this allows for AdSense integration on each article. See documentation above to properly format your object. */
  adsense: PropTypes.object,
  hideAuthor: PropTypes.bool
};
GhostArticle.defaultProps = {
  failureText: 'Something went wrong while loading your article. Please try again later.',
  colors: {
    background: '#ffffff',
    backgroundalt: '#242526',
    line: '#242526',
    accent: '#d4d4d5',
    link: '#1264fc',
    featuredbg: '#f7f7f7'
  },
  fonts: {
    title: 'Arial, sans-serif',
    body: 'Arial, sans-serif'
  },
  hideAuthor: false
};
export default GhostArticle;